import { useEffect, useMemo, useState } from "react";
import { api, apiError } from "@/lib/api";
import { euro, PAYMENT_LABELS } from "@/lib/format";
import { PageHeader, StatusBadge } from "@/components/common";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Search, Minus, Plus, User, Store, Home, AlertTriangle, PackageCheck, Repeat, Check, X } from "lucide-react";
import { toast } from "sonner";

function Stepper({ value, onChange, testId }) {
  return (
    <div className="flex items-center gap-1">
      <button type="button" data-testid={`${testId}-minus`} onClick={() => onChange(Math.max(0, value - 1))} className="w-8 h-8 rounded-lg bg-slate-100 hover:bg-slate-200 flex items-center justify-center"><Minus size={14} /></button>
      <span className="w-8 text-center font-bold text-[#0B2447]" data-testid={`${testId}-val`}>{value}</span>
      <button type="button" data-testid={`${testId}-plus`} onClick={() => onChange(value + 1)} className="w-8 h-8 rounded-lg bg-[#E4F5FB] text-[#0EA5C4] hover:bg-cyan-100 flex items-center justify-center"><Plus size={14} /></button>
    </div>
  );
}

export default function NewExchange() {
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const [detail, setDetail] = useState(null);
  const [price, setPrice] = useState([]);
  const [delivered, setDelivered] = useState({});
  const [returned, setReturned] = useState({});
  const [method, setMethod] = useState("cash");
  const [busy, setBusy] = useState(false);

  useEffect(() => { api.get("/catalog/price-list").then((r) => setPrice(r.data)); }, []);

  useEffect(() => {
    if (q.trim().length < 1) { setResults([]); return; }
    const t = setTimeout(() => api.get(`/customers?q=${encodeURIComponent(q)}`).then((r) => setResults(r.data.slice(0, 8))), 250);
    return () => clearTimeout(t);
  }, [q]);

  const open = async (c) => {
    setResults([]); setQ("");
    const { data } = await api.get(`/customers/${c.id}`);
    setDetail(data); setDelivered({}); setReturned({});
  };

  const total = useMemo(() => price.reduce((s, p) => s + (delivered[p.kit_id] || 0) * p.price_cents, 0), [delivered, price]);
  const deliveredCount = Object.values(delivered).reduce((a, b) => a + b, 0);
  const returnedCount = Object.values(returned).reduce((a, b) => a + b, 0);

  const confirm = async () => {
    if (deliveredCount === 0 && returnedCount === 0) { toast.error("Seleziona almeno un kit"); return; }
    setBusy(true);
    try {
      const body = {
        customer_id: detail.customer.id,
        delivered_items: price.filter((p) => delivered[p.kit_id]).map((p) => ({ kit_id: p.kit_id, qty: delivered[p.kit_id] })),
        returned_items: price.filter((p) => returned[p.kit_id]).map((p) => ({ kit_id: p.kit_id, qty: returned[p.kit_id] })),
        payment_method: method,
      };
      const { data } = await api.post("/exchanges", body);
      if (method === "stripe") {
        const co = await api.post("/payments/checkout", { exchange_id: data.exchange.id, origin_url: window.location.origin });
        window.location.href = co.data.checkout_url;
        return;
      }
      toast.success(`Cambio ${data.exchange.code} confermato · ${euro(data.exchange.total_cents)}`);
      const refreshed = await api.get(`/customers/${detail.customer.id}`);
      setDetail(refreshed.data); setDelivered({}); setReturned({});
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };

  const c = detail?.customer;

  return (
    <div>
      <PageHeader title="Nuovo Cambio" subtitle="Cerca il cliente e registra il cambio in pochi secondi" />

      {!detail && (
        <Card className="p-6 max-w-2xl">
          <label className="text-sm font-semibold text-[#0B2447]">Cerca cliente</label>
          <div className="relative mt-2">
            <Search size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <Input autoFocus data-testid="exchange-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Telefono, nome, email o codice…" className="pl-10 h-12 text-base" />
          </div>
          <div className="mt-2 divide-y">
            {results.map((r) => (
              <button key={r.id} data-testid={`search-result-${r.code}`} onClick={() => open(r)} className="w-full flex items-center gap-3 py-3 hover:bg-slate-50 px-2 rounded-lg text-left">
                <span className={`p-2 rounded-lg ${r.type === "host" ? "bg-[#E4F5FB] text-[#0EA5C4]" : "bg-slate-100 text-slate-500"}`}>{r.type === "host" ? <Store size={16} /> : <Home size={16} />}</span>
                <div className="flex-1"><p className="font-semibold text-[#0B2447]">{r.display_name}</p><p className="text-xs text-slate-400">{r.code} · {r.phone}</p></div>
                <span className="text-xs uppercase font-bold text-slate-400">{r.type}</span>
              </button>
            ))}
            {q && results.length === 0 && <p className="py-4 text-sm text-slate-400">Nessun cliente trovato.</p>}
          </div>
        </Card>
      )}

      {detail && (
        <div className="grid lg:grid-cols-3 gap-6">
          <div className="lg:col-span-1 space-y-4">
            <Card className="p-5">
              <div className="flex items-start justify-between">
                <div className="flex items-center gap-3">
                  <span className={`p-2.5 rounded-xl ${c.type === "host" ? "bg-[#E4F5FB] text-[#0EA5C4]" : "bg-slate-100 text-slate-500"}`}>{c.type === "host" ? <Store size={20} /> : <User size={20} />}</span>
                  <div>
                    <p className="font-display font-bold text-lg text-[#0B2447]">{c.display_name}</p>
                    <p className="text-xs text-slate-400">{c.code} · {c.type.toUpperCase()} · {c.phone}</p>
                  </div>
                </div>
                <button data-testid="exchange-change-customer" onClick={() => setDetail(null)} className="text-slate-400 hover:text-slate-600"><X size={18} /></button>
              </div>
              <div className="grid grid-cols-2 gap-3 mt-4">
                <div className="p-3 rounded-xl bg-slate-50"><p className="text-xs text-slate-400">Cauzione disponibile</p><p className="stat-num text-xl text-[#0B2447]">{euro(detail.deposit.available_cents)}</p></div>
                <div className="p-3 rounded-xl bg-slate-50"><p className="text-xs text-slate-400">Valore dotazione</p><p className="stat-num text-xl text-[#0B2447]">{euro(detail.allocation.total_value_cents)}</p></div>
              </div>
              <div className={`mt-3 text-xs font-semibold px-3 py-2 rounded-lg flex items-center gap-2 ${detail.deposit_covers_allocation ? "bg-green-50 text-green-700" : "bg-orange-50 text-orange-700"}`}>
                {detail.deposit_covers_allocation ? <Check size={14} /> : <AlertTriangle size={14} />}
                {detail.deposit_covers_allocation ? "Cauzione copre la dotazione" : "Cauzione insufficiente per la dotazione"}
              </div>
            </Card>

            {detail.open_anomalies.length > 0 && (
              <Card className="p-4 border-orange-200 bg-orange-50/50">
                <p className="text-sm font-semibold text-orange-700 flex items-center gap-2"><AlertTriangle size={16} />Anomalie aperte: {detail.open_anomalies.length}</p>
              </Card>
            )}
            {detail.pending_returns.length > 0 && (
              <Card className="p-4 border-cyan-200 bg-cyan-50/50">
                <p className="text-sm font-semibold text-[#0EA5C4] flex items-center gap-2"><PackageCheck size={16} />Restituzioni da verificare: {detail.pending_returns.length}</p>
              </Card>
            )}
          </div>

          <div className="lg:col-span-2 space-y-4">
            <div className="grid md:grid-cols-2 gap-4">
              <Card className="p-5">
                <p className="font-display font-bold text-[#0B2447] mb-3">Cosa rientra <span className="text-xs font-normal text-slate-400">(sporco)</span></p>
                <div className="space-y-2">
                  {price.map((p) => (
                    <div key={p.kit_id} className="flex items-center justify-between">
                      <span className="text-sm text-slate-600">{p.name}</span>
                      <Stepper testId={`ret-${p.kit_id}`} value={returned[p.kit_id] || 0} onChange={(v) => setReturned({ ...returned, [p.kit_id]: v })} />
                    </div>
                  ))}
                </div>
              </Card>
              <Card className="p-5">
                <p className="font-display font-bold text-[#0B2447] mb-3">Cosa consegna <span className="text-xs font-normal text-slate-400">(pulito)</span></p>
                <div className="space-y-2">
                  {price.map((p) => (
                    <div key={p.kit_id} className="flex items-center justify-between">
                      <div><span className="text-sm text-slate-600">{p.name}</span><span className="text-xs text-slate-400 ml-1">{euro(p.price_cents)}</span></div>
                      <Stepper testId={`del-${p.kit_id}`} value={delivered[p.kit_id] || 0} onChange={(v) => setDelivered({ ...delivered, [p.kit_id]: v })} />
                    </div>
                  ))}
                </div>
              </Card>
            </div>

            <Card className="p-5">
              <div className="flex flex-col sm:flex-row sm:items-center gap-4">
                <div className="flex-1">
                  <p className="text-sm text-slate-400">Totale servizio</p>
                  <p className="stat-num text-4xl text-[#0B2447]" data-testid="exchange-total">{euro(total)}</p>
                </div>
                <div className="w-full sm:w-48">
                  <p className="text-sm text-slate-400 mb-1">Pagamento</p>
                  <Select value={method} onValueChange={setMethod}>
                    <SelectTrigger data-testid="exchange-payment"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {Object.entries(PAYMENT_LABELS).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <Button onClick={confirm} disabled={busy} data-testid="exchange-confirm" className="h-14 px-8 text-base bg-[#16a34a] hover:bg-green-700 sm:self-end">
                  <Repeat size={18} className="mr-2" />Conferma Cambio
                </Button>
              </div>
            </Card>
          </div>
        </div>
      )}
    </div>
  );
}
